import type { ChipKind, ChipStatus } from './chip-view-model';

/**
 * The chip's leading marks — which glyphs, in which tones, in which order, and
 * which survive a narrow lane. Pure, so the one rule the month grid, the
 * week/day lanes and the day panel share is decided in exactly one place.
 *
 * The standing rule: once a send has RESOLVED, the slot says how it went (the
 * red failure mark, the green tick); until then it says what the thing is. That
 * rule assumes the title tells you what the thing is — true for a message, a
 * poll and a campaign, which is why their kind glyph can be given up.
 *
 * It is NOT true for a WhatsApp Status: its title is its own text and reads
 * exactly like a message to a person, so the Status ring must stay. An earlier
 * version merged the two — the ring turned red with a "!" inside — and on
 * screen that read as a failure glyph, not as a Status: the owner could no
 * longer see the kind. So they are TWO marks now:
 *
 *   [failure mark][Status ring]  03:07  ffff
 *
 * - The outcome mark is the SAME glyph, in the SAME tone, that every other
 *   resolved chip carries, and it sits in the same place — the leading edge — so
 *   a column of failures lines up whatever kind each one is.
 * - The ring keeps its identity colour in every state; it is never tinted by
 *   the outcome. Red means failed, and only the failure mark is red.
 * - On a narrow lane the ring is dropped first and the outcome last; a Status
 *   that has not resolved keeps its ring, because then it is the only mark.
 */

export type LeadGlyph =
    /** what it is */
    | 'message' | 'poll' | 'campaign' | 'status'
    /** how it went */
    | 'failed' | 'sent' | 'unconfirmed';

/** Which colour role a mark takes. `identity` is the item's own rail colour;
 *  the others are the calendar's state marks (see calendar-grid.module.scss). */
export type LeadTone = 'identity' | 'bad' | 'ok' | 'quiet';

export interface LeadMark {
    glyph: LeadGlyph;
    tone: LeadTone;
    /** What the mark is announced as. Absent for a kind glyph that only repeats
     *  what the title already says, which stays `aria-hidden`. */
    labelKey?: string;
}

type Outcome = Exclude<ChipStatus, 'pending'>;

const OUTCOME: Record<Outcome, LeadMark> = {
    failed: { glyph: 'failed', tone: 'bad', labelKey: 'calendar.grid.didNotSend' },
    sent: { glyph: 'sent', tone: 'ok', labelKey: 'calendar.grid.wasSent' },
    unconfirmed: { glyph: 'unconfirmed', tone: 'quiet', labelKey: 'calendar.grid.sentUnconfirmed' },
};

/** The same marks, announced as what happened to a Status. */
const STATUS_OUTCOME: Record<Outcome, LeadMark> = {
    failed: { glyph: 'failed', tone: 'bad', labelKey: 'calendar.grid.statusDidNotPost' },
    sent: { glyph: 'sent', tone: 'ok', labelKey: 'calendar.grid.statusWasPosted' },
    unconfirmed: { glyph: 'unconfirmed', tone: 'quiet', labelKey: 'calendar.grid.statusPostedUnconfirmed' },
};

const STATUS_RING: LeadMark = { glyph: 'status', tone: 'identity', labelKey: 'calendar.grid.statusKind' };

/**
 * @param textShown whether the chip has room for its title. A narrow lane sheds
 *   the kind glyph with the title; outcomes are never shed.
 */
export function planLeadMarks(kind: ChipKind, status: ChipStatus, textShown: boolean): LeadMark[] {
    const outcome: Outcome | null = status === 'pending' ? null : status;

    if (kind === 'status') {
        if (!outcome) return [STATUS_RING];
        return textShown ? [STATUS_OUTCOME[outcome], STATUS_RING] : [STATUS_OUTCOME[outcome]];
    }

    if (outcome) return [OUTCOME[outcome]];
    return textShown ? [{ glyph: kind, tone: 'identity' }] : [];
}
